
import { useState } from "react";
import { Investor, Contact } from "@/types/investor";
import { hasInvestorBeenEmailed } from "@/utils/emailUtils";
import { useDraftActions } from "./emails/useDraftActions";
import { useClipboardActions } from "./emails/useClipboardActions";
import { useSendEmail } from "./emails/useSendEmail";
import { EmailDraft } from "@/utils/investors/draftUtils";

export interface EmailActionsProps {
  investor: Investor;
  selectedContactData: Contact | null;
  generatedEmail: string;
  setGeneratedEmail: (email: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  businessInfo: string;
  onEmailSent?: () => void;
  emailSent: boolean;
  setEmailSent: (sent: boolean) => void;
}

export const useEmailActions = ({
  investor,
  selectedContactData,
  generatedEmail,
  setGeneratedEmail,
  isLoading,
  setIsLoading,
  businessInfo,
  onEmailSent,
  emailSent,
  setEmailSent
}: EmailActionsProps) => {
  // Check if email was already sent
  useState(() => {
    const alreadySent = hasInvestorBeenEmailed(investor.id);
    if (alreadySent) {
      setEmailSent(true);
    }
  });
  
  // Use the split hooks
  const draftActions = useDraftActions(
    investor.id,
    selectedContactData,
    setGeneratedEmail,
    isLoading,
    setIsLoading,
    businessInfo
  );
  
  const clipboardActions = useClipboardActions(generatedEmail);
  
  const sendEmailActions = useSendEmail(
    investor,
    selectedContactData,
    generatedEmail,
    onEmailSent,
    setEmailSent
  );
  
  // Combine all actions
  return {
    drafts: draftActions.drafts,
    selectedDraftId: draftActions.selectedDraftId,
    showDrafts: draftActions.showDrafts,
    emailSent,
    isSending: sendEmailActions.isSending,
    handleCopyToClipboard: clipboardActions.handleCopyToClipboard,
    handleDownload: clipboardActions.handleDownload,
    handleSaveAsDraft: draftActions.handleSaveAsDraft,
    handleSendEmail: sendEmailActions.handleSendEmail,
    handleSelectDraft: draftActions.handleSelectDraft,
    handleDeleteDraft: draftActions.handleDeleteDraft,
    handleLabelDraft: draftActions.handleLabelDraft,
    handleRegenerateDraft: draftActions.handleRegenerateDraft,
    handleToggleDrafts: draftActions.handleToggleDrafts
  };
};
